import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Commands, Results } from '@local/client-contracts';
import { isEmbed, isLiveCollection, isStaticCollection, isWikiCollection, isExtension } from '@local/common-web';
import { KeyName, capitalCase } from '@local/ts-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { DateFormat } from '@shared/consts';
import { HighlightStyle } from '@shared/pipes';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { isDynamicCommand, isIcon, isRightClick, windowSizeObserver } from '@shared/utils';
import { getTimeFromNowInText } from '@shared/utils/date-format.util';
import { isEqual } from 'lodash';
import moment from 'moment';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { WorkspacesService } from 'src/app/bar/services';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { CollectionsUtilService } from 'src/app/bar/services/collections-util.service';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { ContextMenu } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { HomePinsService } from 'src/app/bar/services/home-pins.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { CollectionItem, ResultItem } from '../../../results';
import { ShortcutHandler } from '../../../results-views/view-item/view-item.component';
import { PageType } from '../../../results/models/view-filters';
import { CollectionContextMenuHelper } from '../../helpers/collection-context-menu-helper';
import { COLLECTION_TYPE_TO_ICON_MAP } from '../../helpers/collection-type-icon-map';
import { collectionContent } from '../../helpers/collection.content';
import { CollectionContextMenuPlace, CollectionOptionsId, CollectionTag } from '../../models';
import { CollectionViewMode } from '../../models/collection-view-mode';
import { WikiDraftsService } from 'src/app/bar/services/wikis/wiki-drafts.service';
import { Constants } from '@local/common';

const showResultSectionsDefault: Results.ShowResultSections = {
  showBullets: true,
  showContextMenu: true,
  showFavoriteIcon: true,
  showSubtitleInSecondLine: true,
};

declare type TagDisplay = {
  index: number;
  size: number;
  value: string;
  show: boolean;
  extraTag: boolean;
};

type ButtonType = 'favorite' | 'square' | 'newIndication';

const BUTTONS_WIDTH: Record<ButtonType, number> = {
  favorite: 14,
  square: 24,
  newIndication: 16,
};

const BUTTONS_MARGIN_ALL = 16;
const BUTTONS_MARGIN_ITEM = 8;

@UntilDestroy()
@Component({
  selector: 'collection-item',
  templateUrl: './collection-item.component.html',
  styleUrls: ['./collection-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionItemComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly RESULT_VIEW_MAX_TAGS_LENGTH = 3;
  private resultCountType: 'Result' | 'Card' | 'Draft';
  private _showResultSections: Results.ShowResultSections;
  private _collection: CollectionItem;
  private _viewMode: CollectionViewMode;
  private collectionContextMenuHelper: CollectionContextMenuHelper;
  private isCreatedCollection: boolean;
  private tagsDisplayTemp: TagDisplay[];
  private _resultAmount: number;
  readonly ALL_CARDS_ID = 'all-cards';
  readonly ALL_DRAFTS_ID = 'all-drafts';

  bullets: Results.Bullet[];
  displayModifiedTime: string;
  preDisplayModifiedTime: string;
  tooltipModifiedTime: string;
  contextMenuActive: boolean;
  resultsCount: number | string;
  highlightStyle: HighlightStyle = 'default';
  avatarUrl: string;
  tagsToDisplay: CollectionTag[];
  hiddenTagsValues: string[];
  avatarsList: AvatarItemModel[] = [];
  extraTagValue: string;
  owner: AvatarItemModel;
  isFavorite: boolean;
  collectionIcon: { icon: string; name: string; class?: string };
  shortcutsHandlers: ShortcutHandler[];
  pinTooltip: string;
  isLauncher: boolean;
  isEmbed = isEmbed();
  isExtension = isExtension();
  defaultIcon: string;
  isViewPageItem: boolean;
  ownerInfoTooltip: string;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  @Input() model: ResultItem;
  @Input() selected: boolean;
  @Input() checked: boolean;
  @Input() componentFocused: boolean;
  @Input() isClickable = true;
  @Input() tabId: string;
  @Input() sortPopupOpened: boolean;
  @Input() index: number;
  @Input() layoutMode: Results.LayoutType;
  @Input() actionsReadOnly: boolean;
  @Input() pageType: PageType;

  @Input() set resultAmount(val: number) {
    if (this._resultAmount === val) return;
    this._resultAmount = val;
    this.updateResultsCount();
  }
  @Input() set showResultSections(value: Results.ShowResultSections) {
    this._showResultSections = {
      ...showResultSectionsDefault,
      ...value,
      showNewIndication: this.collection?.showResultSections?.showNewIndication,
    };
  }
  @Input() set collection(collection: CollectionItem) {
    if (!collection) return;
    const prevCollection = this.collection;
    this._collection = collection;
    if (this.collection) {
      this.setViewType();
      if (this.collection.modifiedTime) {
        this.isCreatedCollection = this.collection.modifiedTime === this.collection.createdTime;
        this.displayModifiedTime = capitalCase(getTimeFromNowInText(this.collection?.modifiedTime)).toLowerCase();
        this.preDisplayModifiedTime = this.isCreatedCollection
          ? capitalCase(collectionContent.created) + ' '
          : capitalCase(collectionContent.updated) + ' ';
        this.tooltipModifiedTime = moment(this.isCreatedCollection ? this.collection.createdTime : this.collection.modifiedTime).format(
          DateFormat.FULL_DATE_HOURS_A
        );
      }
      if (prevCollection?.resultsCount !== this.collection.resultsCount) {
        this.updateResultsCount();
      }

      if (!prevCollection || !isEqual(prevCollection?.id, collection?.id)) {
        this.initTagsToDisplay();
      }
      this.buildBullets();

      this.isFavorite = !!this.collection?.favoriteMarkedTime;

      if (this.collection.shareOptions && !isEqual(prevCollection?.shareOptions, this.collection.shareOptions)) {
        this.getAvatarList();
      }

      if (!this.collection.shareOptions) {
        if (this.collection.isShared) {
          this.collection.shareOptions = prevCollection?.shareOptions;
        } else {
          this.avatarsList = [];
        }
      }

      this.setPinTooltip();
      this.getCollectionAvatar();
      this.getCollectionIcon();
      this.setShortcutsHandlers();
      this.cdr.markForCheck();
    }
  }
  @Input() set viewMode(val: CollectionViewMode) {
    this._viewMode = val ?? 'search-result';
  }
  @Input() set contextMenu(val: ContextMenu) {
    if (!val || this.collection?.id !== val.itemId) return;
    this.openContextMenu(val.event);
  }

  @Output() changeSelection = new EventEmitter();
  @Output() contextOpen = new EventEmitter();
  @Output() contextClose = new EventEmitter();
  @Output() invoke = new EventEmitter();
  @Output() layoutChanged = new EventEmitter();
  @Output() tagClicked = new EventEmitter<CollectionTag>();
  @Output() onClickEvent = new EventEmitter();
  @Output() duplicateCollection = new EventEmitter();

  @ViewChild('collectionTitle', { read: ElementRef }) collectionTitleRef: ElementRef;
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;
  @ViewChild('tags', { read: ElementRef }) tagsRef: ElementRef;

  @HostBinding('attr.viewMode') get collectionViewMode() {
    return this._viewMode;
  }

  @HostBinding('style.--buttonWidth')
  buttonsWidth: string;

  @HostBinding('class.selected')
  get isSelected() {
    return this.selected;
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  get resultAmount() {
    return this._resultAmount;
  }

  get showResultSections(): Results.ShowResultSections {
    return this._showResultSections;
  }

  get collection(): CollectionItem {
    return this._collection;
  }

  get isCardView(): boolean {
    return this._viewMode === 'card';
  }

  get isWidgetView(): boolean {
    return this._viewMode === 'widget';
  }

  get isSearchResultView(): boolean {
    return this._viewMode === 'search-result' || this._viewMode === 'collection-search-result' || this._viewMode === 'list';
  }

  get isGalleryCardView(): boolean {
    return this._viewMode === 'gallery-card';
  }

  get viewMode() {
    return this._viewMode;
  }

  get tooltip() {
    if (isLiveCollection(this.collection)) return this.collectionsService.createSearchParamsSummary(this.collection);
  }

  get resultsCountDescription(): string {
    return `${this.resultsCount} ${+this.resultsCount > 1 ? this.resultCountType + 's' : this.resultCountType}`;
  }

  get collectionTypeIconMap() {
    return COLLECTION_TYPE_TO_ICON_MAP;
  }

  constructor(
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    public collectionsService: CollectionsService,
    private eventsService: EventsService,
    private hubService: HubService,
    private collectionsHelperService: CollectionsUtilService,
    private homePinsService: HomePinsService,
    private workspaceService: WorkspacesService,
    private avatarListService: AvatarListService,
    private wikiCardsService: WikiCardsService,
    private wikiDraftsService: WikiDraftsService
  ) {
    this.hubService.isLauncher$.pipe(untilDestroyed(this)).subscribe((l) => (this.isLauncher = l));
    windowSizeObserver()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.buildTags();
      });
  }

  ngOnInit(): void {
    this.showResultSections = {
      ...showResultSectionsDefault,
      ...this.showResultSections,
      showNewIndication: this.collection?.showResultSections?.showNewIndication,
    };
    this.highlightStyle = this.isEmbed || this.isLauncher || this.hubService.currentLocation === 'home' ? 'bold' : 'default';
    this.calculateButtonsWidth();
  }

  ngAfterViewInit(): void {
    this.initContextMenuHelper();
    this.initContextMenuHelperListeners();
    this.tagsDisplayTemp = this.buildTempTags();
    this.buildTags();
  }

  ngOnDestroy(): void {
    this.collectionContextMenuHelper?.ref?.destroy();
  }

  @HostListener('auxclick', ['$event']) onAuxClick(event: MouseEvent) {
    if (this.isViewPageItem) {
      return;
    }
    if (isRightClick(event) && !this.showResultSections.disableRightClick) {
      return this.openContextMenu(event);
    }
  }

  @HostListener('click', ['$event']) async onClick(event: MouseEvent) {
    if (this.selected) {
      await this.openCollection();
    }
    if (!this.isClickable) return;
    event.preventDefault();
    this.onClickEvent.emit(this.model);
  }

  @HostListener('dblclick', ['$event']) onDblClick(event: MouseEvent) {
    this.openCollection();
    event.preventDefault();
  }

  private setViewType() {
    this.isViewPageItem = [this.ALL_CARDS_ID, this.ALL_DRAFTS_ID].includes(this.collection.id);

    switch (this.collection.id) {
      case this.ALL_CARDS_ID:
        this.defaultIcon = 'icon-lot-of-cards';
        this.resultCountType = 'Card';
        break;
      case this.ALL_DRAFTS_ID:
        this.defaultIcon = 'icon-draft';
        this.resultCountType = 'Draft';
        break;
      default:
        this.defaultIcon = COLLECTION_TYPE_TO_ICON_MAP[this.collection.kind];
        this.resultCountType = !isWikiCollection(this.collection) ? 'Result' : 'Card';
        break;
    }
  }

  private buildTags() {
    if (!this.tagsRef || !this.tagsDisplayTemp?.length) return;

    setTimeout(() => {
      if (!this.tagsRef || !this.tagsDisplayTemp.length) return;

      const tagElements: HTMLElement[] = [...this.tagsRef.nativeElement.children];
      const standardTags = this.tagsDisplayTemp.filter((tag) => !tag.extraTag);

      if (this.isSearchResultView) {
        const tagsToHide = standardTags.slice(this.RESULT_VIEW_MAX_TAGS_LENGTH, standardTags.length);
        tagsToHide.forEach((t) => {
          t.show = false;
          if (tagElements[t.index]) {
            tagElements[t.index].style.display = 'none';
          }
        });
        this.handleExtraTag(standardTags, tagElements);
        this.extraTagValue = `+${tagsToHide.length}`;
      } else {
        const tagsContainerSize = this.tagsRef.nativeElement.getBoundingClientRect().width;
        const extraTag = this.tagsDisplayTemp.at(-1);
        let sum = 0;
        standardTags.forEach((tag) => {
          sum += tag.size;
          tag.show = sum + extraTag.size < tagsContainerSize;
          tagElements[tag.index].style.display = tag.show ? 'flex' : 'none';
        });
        this.handleExtraTag(standardTags, tagElements);
        this.extraTagValue = `+${standardTags.length - this.tagsDisplayTemp.find((t) => !t.show)?.index}`;
        this.cdr.markForCheck();
      }
    }, 200);
  }

  private setPinTooltip() {
    if (!this.collection.pinBy) return;
    const homePins = this.homePinsService.getAllByPinedId(this.collection.id);
    if (homePins.length > 1) {
      const currentTabsId = homePins?.map((h) => h.tabId);
      const tabNames = this.collectionsService.selectedTabs.filter((s) => currentTabsId.includes(s.id));
      let tabsString = '';
      tabNames.forEach((t) => (tabsString = `${!tabsString.length ? '' : tabsString + ','} "${t.name}"`));
      this.pinTooltip = `Pinned to ${tabsString} Tabs`;
    } else {
      const tabName = this.collectionsService.selectedTabs.find((s) => s.id === homePins[0]?.tabId)?.name;
      this.pinTooltip = `Pinned to "${tabName}" Tab`;
    }
  }

  private handleExtraTag(standardTags: TagDisplay[], tagsDOM: HTMLElement[]) {
    this.hiddenTagsValues = standardTags.filter((t) => !t.show).map((t) => t.value);
    const showExtraTag = !!standardTags.find((t) => !t.show);
    this.tagsDisplayTemp.at(-1).show = showExtraTag;
    tagsDOM.at(-1).style.visibility = showExtraTag ? 'visible' : 'hidden';
  }

  private buildTempTags(): TagDisplay[] {
    const tags = this.tagsRef?.nativeElement?.children;
    if (!tags) return;
    return [...tags].map((tag: HTMLElement) => {
      return {
        index: +tag.id,
        size: tag.getBoundingClientRect().width,
        value: tag.innerText,
        extraTag: tag.classList.contains('extra-tag'),
        show: !tag.classList.contains('extra-tag'),
      };
    });
  }

  protected initContextMenuHelper(): void {
    this.collectionContextMenuHelper = new CollectionContextMenuHelper(
      this.contextMenuService,
      this.keyboardService,
      this.actionsRef,
      this.hubService,
      this.workspaceService
    );
  }

  private updateResultsCount() {
    const count = this.resultAmount ?? this.collection?.resultsCount;
    this.resultsCount = count === 10000 ? '10,000+' : count;
    this.cdr.markForCheck();
  }

  initContextMenuHelperListeners() {
    this.collectionContextMenuHelper.invoke$.pipe(untilDestroyed(this)).subscribe(({ item, trigger }) => {
      if (isDynamicCommand(item.command)) {
        this.invoke.emit(item);
      } else {
        this.collectionsService.onContextMenuCommand(item, trigger, this.collection, this.viewMode);
        if (item.id === 'favorite') {
          this.isFavorite = !this.isFavorite;
          this.cdr.markForCheck();
        } else if (item.id === 'change_layout') {
          this.layoutChanged.emit((item.command as Commands.DynamicCommand).value);
        } else if (item.id === 'make_a_copy') {
          this.duplicateCollection.emit();
        }
      }
    });
  }

  private initTagsToDisplay() {
    this.tagsToDisplay = this.collection.tags?.map((t) => ({ value: t, type: 'tag' }));
  }

  private async getCollectionAvatar() {
    this.owner = await this.avatarListService.getOwnerAvatar(this.collection.accountId);
    this.avatarUrl = <string>this.owner.imgUrl || this.owner.name;
    this.owner.name = this.owner.name?.replace('(Owner)', '');
    this.ownerInfoTooltip = `${this.preDisplayModifiedTime} by ${this.owner?.name} ${this.displayModifiedTime}`;
    this.cdr.markForCheck();
  }

  async getAvatarList() {
    this.avatarsList = await this.avatarListService.createAvatarList(this.collection.accountId, this.collection.shareOptions);
    this.cdr.markForCheck();
  }

  private async buildBullets() {
    this.bullets = [];
    const accountId = this.collection.modifiedBy;
    const avatar = await this.avatarListService.getOwnerAvatar(accountId);
    let startModifiedPhrase;
    if (this.workspaceService.isMe(accountId)) {
      startModifiedPhrase = 'you';
    } else {
      startModifiedPhrase = avatar.name.replace('(Owner)', '');
    }

    if (this.collection?.modifiedTime) {
      let tooltip = moment(this.collection.modifiedTime).format(DateFormat.FULL_DATE_HOURS_A);
      if (avatar.isNameFromEmail) {
        tooltip = `${avatar.email} ${tooltip}`;
      }
      this.bullets.push({
        parts: [
          {
            text: `${this.preDisplayModifiedTime} by ${startModifiedPhrase} ${this.displayModifiedTime}`,
            icon: { lightUrl: isIcon(avatar.imgUrl) ? avatar.imgUrl.lightUrl : avatar.imgUrl, rounded: true, size: 'large' },
            tooltip,
          },
        ],
      });
    }

    const resultsCount = this.collection.resultsCount;
    this.bullets.push({
      parts: [
        {
          text: `${resultsCount || 0} ${this.resultCountType}${resultsCount === 1 ? '' : 's'}`,
          icon: null,
          tooltip: '',
        },
      ],
    });
  }

  private getViewModeName(canEdit: boolean): CollectionContextMenuPlace {
    const viewerMode: CollectionContextMenuPlace = canEdit ? this.collectionViewMode : `${this.collectionViewMode}-view`;
    if (!viewerMode.startsWith('search-result') || !isWikiCollection(this.collection)) {
      return viewerMode;
    }
    return `${viewerMode}-wiki` as CollectionContextMenuPlace;
  }

  private getViewMode(widgetViewMode): CollectionContextMenuPlace {
    const canEdit = this.collectionsHelperService.canEdit(
      this.collection,
      widgetViewMode ? 'pin' : 'collection',
      widgetViewMode ? this.tabId : null
    );
    return this.getViewModeName(canEdit);
  }

  async openContextMenu(event: MouseEvent) {
    const widgetViewMode = this.collectionViewMode === 'widget';
    const viewMode: CollectionContextMenuPlace = this.getViewMode(widgetViewMode);
    const removeItems: CollectionOptionsId[] = [];
    if (isWikiCollection(this.collection)) {
      removeItems.push('make_a_copy');
    } else {
      removeItems.push('verification_details');
    }
    if (!this.collectionsService.isOwnerOrAdmin) {
      removeItems.push('pin');
      removeItems.push('change_layout');
    }
    if (this.actionsReadOnly) {
      removeItems.push('delete');
    }
    if (this.pageType !== 'search') {
      removeItems.push('search_in_collection');
    }
    const ref = await this.collectionContextMenuHelper.open(
      event,
      { pinBy: this.collection?.pinBy },
      viewMode,
      this.isFavorite,
      removeItems,
      [],
      this.layoutMode
    );
    this.contextMenuActive = true;
    this.contextOpen.emit();
    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuActive = false;
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.eventsService.event('collections.open_menu', {
      label: 'mouse_click',
      location: { title: this.hubService.currentLocation },
    });
    this.cdr.markForCheck();
  }

  getCollectionIcon() {
    if (isLiveCollection(this.collection)) {
      this.collectionIcon = {
        icon: 'icon-live-collection',
        name: 'Live Collection',
        class: 'live-collection-icon',
      };
    } else if (isStaticCollection(this.collection)) {
      this.collectionIcon = {
        icon: 'icon-curated',
        name: 'Curated Collection',
      };
    } else if (isWikiCollection(this.collection)) {
      this.collectionIcon = {
        icon: 'icon-wiki',
        name: 'Wiki Collection',
      };
    }
  }

  async openCollection() {
    if (this.collection.id === this.ALL_CARDS_ID) {
      await this.wikiCardsService.openAllCards();
      return;
    }
    if (this.collection.id === this.ALL_DRAFTS_ID) {
      await this.wikiDraftsService.openAllDrafts();
      return;
    }
    const state = this.isLauncher || (this.isEmbed && !this.isExtension) ? 'search-view' : 'collections-view';
    this.collectionsService.openCollection(this.collection, state);
  }

  get tooltipText() {
    const titleEl: HTMLElement = this.collectionTitleRef?.nativeElement;
    if (!titleEl) return;
    const height = titleEl.clientHeight;
    const scrollHeight = titleEl.scrollHeight;
    return height + 3 < scrollHeight ? this.collection?.title : '';
  }

  async setShortcutsHandlers() {
    const shortcuts: any = await this.keyboardService.loadShortcuts<any>(['_collection']);
    this.shortcutsHandlers = [
      {
        fn: (item: CollectionItemComponent) => this.collectionsService.openCollection(item.collection, 'collections-view'),
        keyBinding: <KeyName[]>shortcuts?.open.map((k) => k.toLowerCase()),
      },
      {
        fn: (item: CollectionItemComponent) => this.collectionsService.copyUrl(item.collection),
        keyBinding: <KeyName[]>shortcuts?.copyUrl.map((k) => k.toLowerCase()),
      },
      {
        fn: (item: CollectionItemComponent) => this.collectionsService.toggleFavorite(item.collection),
        keyBinding: shortcuts?.favorite as Array<KeyName>,
      },
    ];
  }

  galleryInvoke($event) {
    switch ($event.type) {
      case 'contextmenu':
        $event.event.stopPropagation();
        this.openContextMenu($event.event);
        break;
      case 'checkbox':
        this.changeSelection.emit($event.event);
        break;
    }
  }

  calculateButtonsWidth() {
    let width = BUTTONS_WIDTH.square;
    let countButtons = 1;
    if (this.showResultSections.showFavoriteAction) {
      width += BUTTONS_WIDTH.favorite;
      countButtons++;
    }
    if (this.showResultSections.showNewIndication) {
      width += BUTTONS_WIDTH.newIndication;
      countButtons++;
    }
    width += BUTTONS_MARGIN_ALL;
    width += (countButtons - 1) * BUTTONS_MARGIN_ITEM;
    this.buttonsWidth = `${width}px`;
    this.cdr.markForCheck();
  }

  onTagClick(event: MouseEvent, tag: CollectionTag) {
    this.tagClicked.emit(tag);
    event.stopPropagation();
  }

  async openCollectionItem(event: MouseEvent) {
    event.stopPropagation();
    await this.openCollection();
    this.onClickEvent.emit(this.model);
  }
}
